// The doctor's side of the 3D setup: look at the plan, mark what should change, approve or send it
// back. This is the screen the whole setup feature exists for.
import { useCallback, useEffect, useState } from 'react';
import { SETUP_SOURCE_LABELS, formatDateTime, type SetupView } from '@alignova/shared';
import { SetupWorkspace } from '../../ui/SetupWorkspace.tsx';
import { EmptyState } from '../../ui/primitives.tsx';
import { Skeleton } from '../../ui/mobile.tsx';
import { portalApi } from './portalApi.ts';

export function PortalSetupTab({ caseId }: { caseId: string }) {
  const [view, setView] = useState<SetupView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    portalApi
      .setup(caseId)
      .then((v) => {
        setView(v);
        setLoaded(true);
      })
      .catch((e: Error) => {
        setErr(e.message);
        setLoaded(true);
      });
  }, [caseId]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <Skeleton className="h-64 w-full" />;
  if (err) return <div className="text-sm text-status-urgent-fg">{err}</div>;
  if (!view)
    return (
      <EmptyState
        title="No setup to review yet"
        body="When the lab has planned the tooth movement they'll share the 3D setup here for you to approve."
      />
    );

  return (
    <div className="space-y-4">
      <header className="rounded-xl border border-line bg-surface p-4 shadow-level-1">
        <h2 className="text-base font-semibold text-ink">
          Treatment setup r{view.setup.revision}
        </h2>
        <p className="mt-0.5 text-xs text-muted">
          {view.patientName} · {view.setup.stepCount} aligner steps · planned in{' '}
          {SETUP_SOURCE_LABELS[view.setup.source]}
          {view.setup.sharedAt ? ` · sent to you ${formatDateTime(view.setup.sharedAt)}` : ''}
        </p>
        {view.setup.notes && <p className="mt-2 text-sm text-ink">{view.setup.notes}</p>}
        <p className="mt-2 text-sm text-muted">
          Drag to rotate, scroll to zoom, and drag the slider to watch the teeth move step by step.
          Tap a tooth to mark a change on it.
        </p>
      </header>

      <SetupWorkspace
        view={view}
        busy={busy}
        onAnnotate={(input) => run(() => portalApi.annotateSetup(view.setup.id, input))}
        onDecide={
          view.canDecide
            ? (decision, comment) => run(() => portalApi.decideSetup(view.setup.id, decision, comment))
            : undefined
        }
      />
    </div>
  );
}
